import React from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { RangeSlider, TextField } from "@shopify/polaris"
import { LIFETIME_HYPOTHESES_CONFIG } from "utils/constants/recommendations/lifetimeHypotheses"
import { EQUIPMENT_TYPE_MAP } from "utils/constants/equipmentTypes"

const LeversMolecule = ({ items }) => {
  const { t } = useTranslation()
  const config = LIFETIME_HYPOTHESES_CONFIG

  // Hard-coded: Polaris RangeSlider thumb width percentage
  const THUMB_WIDTH_PERCENT = 11

  // Helper function to calculate position matching Polaris RangeSlider thumb positioning
  // This ensures all elements (marks, points, labels) align with the center of the slider thumb
  const calculatePosition = (value, min, max) => {
    const range = max - min
    const normalizedValue = (value - min) / range
    const trackWidthPercent = 100 - THUMB_WIDTH_PERCENT
    return normalizedValue * trackWidthPercent + THUMB_WIDTH_PERCENT / 2
  }

  // Get unit with fallback
  const getUnit = () => {
    for (const key of config.unit.translationKeys) {
      const translation = t(key)
      if (translation && translation !== key) {
        return translation
      }
    }
    return config.unit.fallback
  }
  const unit = getUnit()

  // Convert equipment type from integer to enum string key
  const getEquipmentTypeString = (equipmentType) => {
    // If it's already a string key, return it
    if (typeof equipmentType === "string" && !/^\d+$/.test(equipmentType)) {
      return equipmentType
    }
    // Convert integer (or string representation of integer) to enum key
    const intValue =
      typeof equipmentType === "number"
        ? equipmentType
        : parseInt(equipmentType, 10)
    return EQUIPMENT_TYPE_MAP[intValue] || equipmentType
  }

  // Convert string numbers to numbers (e.g., "3.0" -> 3.0)
  const toNumber = (value) => {
    if (value === null || value === undefined) return value
    if (typeof value === "number") return value
    if (typeof value === "string") {
      const parsed = parseFloat(value)
      return isNaN(parsed) ? value : parsed
    }
    return value
  }

  // Get min and max values from data mapping, with fallback to defaults
  const getMinMax = (item) => {
    return {
      min: toNumber(item.min) ?? config.rangeSlider.defaultMin,
      max: toNumber(item.max) ?? config.rangeSlider.defaultMax,
    }
  }

  // Convert date to YYYY-MM-DD format for date input
  const formatDateForInput = (date) => {
    if (!date) return ""

    let dateObj = null

    // If it's already a Date object
    if (date instanceof Date) {
      dateObj = date
    }
    // If it's a string in "year-month-day" format
    else if (typeof date === "string") {
      // Try parsing as "year-month-day" format first
      const parts = date.split("-")
      if (parts.length >= 3) {
        const year = parseInt(parts[0], 10)
        const month = parseInt(parts[1], 10)
        const day = parseInt(parts[2], 10)
        if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
          dateObj = new Date(year, month - 1, day)
        }
      }
      // Fallback: try parsing as Date
      if (!dateObj || isNaN(dateObj.getTime())) {
        dateObj = new Date(date)
      }
    }
    // If it's a number (timestamp)
    else if (typeof date === "number") {
      dateObj = new Date(date)
    }

    // Format as YYYY-MM-DD
    if (dateObj && !isNaN(dateObj.getTime())) {
      const year = dateObj.getFullYear()
      const month = String(dateObj.getMonth() + 1).padStart(2, "0")
      const day = String(dateObj.getDate()).padStart(2, "0")
      return `${year}-${month}-${day}`
    }

    return ""
  }

  // Generate graduation marks for the slider
  const generateGraduationMarks = (
    min,
    max,
    current,
    target,
    marketAverage,
    step = config.rangeSlider.defaultStep,
    displayStep = null
  ) => {
    const marks = []
    const range = max - min
    // Use displayStep for labels, or step if not provided
    const labelStep = displayStep ?? step
    // Create marks based on step (all marks)
    // Start from the first step value >= min
    const start = Math.ceil(min / step) * step
    const end = Math.floor(max / step) * step

    // Helper to check if a value is close to a mark (within step/2)
    const isValueAtMark = (value, markValue) => {
      if (typeof value !== "number" || isNaN(value)) return false
      return Math.abs(value - markValue) < step / 2
    }

    // Helper to check if a mark is on a displayStep
    const isOnDisplayStep = (markValue) => {
      if (labelStep === step) return true // If displayStep equals step, all marks are display marks
      // Check if markValue is a multiple of labelStep (with tolerance)
      const remainder = markValue % labelStep
      return (
        Math.abs(remainder) < step / 2 ||
        Math.abs(remainder - labelStep) < step / 2
      )
    }

    // Generate all marks with the specified step
    for (let i = start; i <= end; i += step) {
      if (i >= min && i <= max) {
        // Calculate position using the helper function to ensure consistency
        const position = calculatePosition(i, min, max)
        const isDisplayMark = isOnDisplayStep(i)
        // Determine color based on which point is at this position
        let textColor = "#6b7280" // default gray
        if (isValueAtMark(current, i)) {
          textColor = config.colors.actuals
        } else if (isValueAtMark(target, i)) {
          textColor = config.colors.target
        } else if (isValueAtMark(marketAverage, i)) {
          textColor = config.colors.market_average
        }

        marks.push({
          value: i,
          position,
          textColor,
          isDisplayMark,
        })
      }
    }
    return marks
  }

  // Internal component for each row to manage its own slider state
  const LeverRow = ({
    equipmentType,
    item,
    index,
    equipmentTypeString,
    calculatePosition,
    unit,
    config,
    t,
    getMinMax,
    toNumber,
    generateGraduationMarks,
    formatDateForInput,
  }) => {
    const { min, max } = getMinMax(item)
    const step = toNumber(item.step) ?? config.rangeSlider.defaultStep
    const displayStep =
      item.displayStep !== undefined
        ? toNumber(item.displayStep)
        : config.rangeSlider.defaultDisplayStep
    const current = toNumber(item.current)
    const target = toNumber(item.target)
    const marketAverage = toNumber(item.market_average)
    const [targetValue, setTargetValue] = React.useState(target || 0)

    // Get target emissions from simulation_proposition_kpi (first occurrence)
    const targetEmissionsKpi =
      item.simulation_proposition_kpis &&
      Array.isArray(item.simulation_proposition_kpis) &&
      item.simulation_proposition_kpis.length > 0
        ? item.simulation_proposition_kpis[0]
        : null
    const targetEmissionsValue = targetEmissionsKpi
      ? toNumber(targetEmissionsKpi.value)
      : null
    const targetEmissionsUnit = targetEmissionsKpi?.unit || null

    // Determine unit based on kpi name: lifetime → "years", allocation → "", else → "%"
    const getDisplayUnit = () => {
      if (!targetEmissionsKpi) return ""
      const kpiName = String(targetEmissionsKpi.name || "").toLowerCase()
      if (kpiName.includes("lifetime")) return "ans"
      if (kpiName.includes("allocation")) return ""
      return "%"
    }
    const displayUnit = getDisplayUnit()

    // Generate graduation marks once
    const graduationMarks = generateGraduationMarks(
      min,
      max,
      current,
      target,
      marketAverage,
      step,
      displayStep
    )
    // Check if values align with graduation marks (using displayStep/2 as tolerance)
    const labelStep = displayStep ?? step
    const tolerance = labelStep / 2
    const currentAlignsWithMark = graduationMarks.some(
      (mark) => Math.abs(current - mark.value) < tolerance
    )
    const targetAlignsWithMark = graduationMarks.some(
      (mark) => Math.abs(target - mark.value) < tolerance
    )
    const targetOnDisplayStep = graduationMarks.some(
      (mark) =>
        mark.isDisplayMark &&
        Math.abs(target - mark.value) < Math.max(step / 2, 0.1)
    )
    const marketAverageAlignsWithMark = graduationMarks.some(
      (mark) => Math.abs(marketAverage - mark.value) < tolerance
    )

    return (
      <tr key={equipmentType || index}>
        <td style={{ textAlign: "center" }}>
          {t(
            `common:equipment_types.${equipmentTypeString}`,
            equipmentTypeString
          )}
        </td>
        <td style={{ textAlign: "center" }}>
          <div style={config.styles.sliderContainer}>
            <div style={{ position: "relative", width: "100%" }}>
              <RangeSlider
                label=""
                min={min}
                max={max}
                step={step}
                value={targetValue || 0}
                onChange={(value) => setTargetValue(value)}
                disabled
                output
              />
            </div>
            {/* Visual markers with points */}
            {typeof current === "number" && !isNaN(current) && (
              <div
                style={{
                  position: "absolute",
                  left: `${calculatePosition(current, min, max)}%`,
                  top: "10px",
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: config.colors.actuals,
                  transform: "translate(-50%, -25%)",
                  zIndex: 10,
                  border: "1px solid white",
                  boxShadow: "0 0 0 1px rgba(0,0,0,0.1)",
                }}
                title={`${t(
                  "recommendation:propositions.levers.current"
                )}: ${current}${displayUnit ? `${displayUnit}` : ""}`}
              />
            )}
            {typeof marketAverage === "number" && !isNaN(marketAverage) && (
              <div
                style={{
                  position: "absolute",
                  left: `${calculatePosition(marketAverage, min, max)}%`,
                  top: "10px",
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: config.colors.market_average,
                  transform: "translate(-50%, -25%)",
                  zIndex: 10,
                  border: "1px solid white",
                  boxShadow: "0 0 0 1px rgba(0,0,0,0.1)",
                }}
                title={`${t(
                  "recommendation:propositions.levers.market_average"
                )}: ${marketAverage}${displayUnit ? `${displayUnit}` : ""}`}
              />
            )}
            {/* Graduation marks below slider */}
            {graduationMarks.map((mark, markIndex) => (
              <div key={markIndex}>
                {/* Graduation line - smaller if not a display mark */}
                <div
                  style={{
                    position: "absolute",
                    left: `${mark.position}%`,
                    top: "24px",
                    width: "1px",
                    height: mark.isDisplayMark ? "8px" : "4px",
                    backgroundColor: "#d1d5db",
                    transform: "translateX(-50%)",
                    zIndex: 0,
                  }}
                />
                {/* Number label below graduation - only for display marks, but not if it's the target (will be shown above) */}
                {mark.isDisplayMark &&
                  !(
                    typeof targetValue === "number" &&
                    !isNaN(targetValue) &&
                    Math.abs(targetValue - mark.value) < step / 2
                  ) && (
                    <div
                      style={{
                        position: "absolute",
                        left: `${mark.position}%`,
                        top: "32px",
                        transform: "translateX(-50%)",
                        fontSize: "11px",
                        color: mark.textColor,
                        fontWeight: "bold",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {mark.value}
                      {displayUnit ? `${displayUnit}` : ""}
                    </div>
                  )}
              </div>
            ))}
            {/* Value labels for points that don't align with graduations */}
            {typeof current === "number" &&
              !isNaN(current) &&
              !currentAlignsWithMark && (
                <div
                  style={{
                    position: "absolute",
                    left: `${calculatePosition(current, min, max)}%`,
                    top: "32px",
                    transform: "translateX(-50%)",
                    fontSize: "11px",
                    color: config.colors.actuals,
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                  }}
                >
                  {current}
                  {displayUnit ? `${displayUnit}` : ""}
                </div>
              )}
            {/* Display target value - always show it above the slider */}
            {typeof targetValue === "number" && !isNaN(targetValue) && (
              <div
                style={{
                  position: "absolute",
                  left: `${calculatePosition(targetValue, min, max)}%`,
                  top: "-25px",
                  transform: "translateX(-50%)",
                  fontSize: "11px",
                  color: config.colors.target,
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                }}
              >
                {targetValue}
                {displayUnit ? `${displayUnit}` : ""}
              </div>
            )}
            {typeof marketAverage === "number" &&
              !isNaN(marketAverage) &&
              !marketAverageAlignsWithMark && (
                <div
                  style={{
                    position: "absolute",
                    left: `${calculatePosition(marketAverage, min, max)}%`,
                    top: "32px",
                    transform: "translateX(-50%)",
                    fontSize: "11px",
                    color: config.colors.market_average,
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                  }}
                >
                  {marketAverage}
                  {displayUnit ? `${displayUnit}` : ""}
                </div>
              )}
          </div>
        </td>
        <td style={{ textAlign: "center" }}>
          {targetEmissionsValue !== null
            ? `${targetEmissionsValue} ${targetEmissionsUnit || ""}`
            : "-"}
        </td>
        <td
          style={{
            textAlign: "center",
          }}
        >
          <div
            className={`year-date-input ${
              typeof target === "number" && !isNaN(target)
                ? "year-date-input-target"
                : ""
            }`}
          >
            <TextField
              type="date"
              value={formatDateForInput(item.year)}
              onChange={() => {}}
              disabled
              label=""
              labelHidden
            />
          </div>
        </td>
      </tr>
    )
  }

  return (
    <div
      className="recommendationsLayout__details__config__hypotheses"
      style={{
        width: "100%",
        display: "block",
        overflowX: "auto",
      }}
    >
      <style>
        {`
          .year-date-input input {
            color: #6b7280 !important;
            font-style: italic !important;
          }
          /* Force the slider track to always be gray instead of purple */
          .Polaris-RangeSlider-SingleThumb__Input {
            --progress-lower: #c4cdd5 !important;
            --progress-upper: #c4cdd5 !important;
          }
          /* Ensure slider track has lower z-index than custom points, but thumb is above points */
          .Polaris-RangeSlider__Track {
            position: relative;
            z-index: 1;
          }
          .Polaris-RangeSlider__Thumb {
            position: relative;
            z-index: 20;
          }
        `}
      </style>
      <table
        className={config.table.className}
        style={{
          width: "100%",
          tableLayout: "fixed",
          minWidth: "100%",
        }}
      >
        <thead>
          <tr>
            <th style={{ width: "17%", textAlign: "center" }}>
              {t("recommendation:propositions.levers.equipment_type")}
            </th>
            <th style={{ width: "38%", textAlign: "center" }}>
              <span
                style={{ color: config.colors.actuals, fontWeight: "bold" }}
              >
                {t("recommendation:propositions.levers.current")}
              </span>{" "}
              /{" "}
              <span style={{ color: config.colors.target, fontWeight: "bold" }}>
                {t("recommendation:propositions.levers.target")}
              </span>{" "}
              /{" "}
              <span
                style={{
                  color: config.colors.market_average,
                  fontWeight: "bold",
                }}
              >
                {t("recommendation:propositions.levers.market_average")}
              </span>
            </th>
            <th style={{ width: "17%", textAlign: "center" }}>
              {t("recommendation:propositions.levers.target_emissions")}
            </th>
            <th style={{ width: "24%", textAlign: "center" }}>
              {t("recommendation:propositions.levers.year")}
            </th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(items).map(([equipmentType, item], index) => {
            const equipmentTypeString = getEquipmentTypeString(
              item.equipment_type
            )
            return (
              <LeverRow
                key={item.equipment_type || index}
                equipmentType={item.equipment_type}
                item={item}
                index={index}
                equipmentTypeString={equipmentTypeString}
                calculatePosition={calculatePosition}
                unit={unit}
                config={config}
                t={t}
                getMinMax={getMinMax}
                toNumber={toNumber}
                generateGraduationMarks={generateGraduationMarks}
                formatDateForInput={formatDateForInput}
              />
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

LeversMolecule.propTypes = {
  items: PropTypes.objectOf(
    PropTypes.shape({
      target: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      current: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      market_average: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      year: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      simulation_proposition_kpi: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
          unit: PropTypes.string,
        })
      ),
      min: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      max: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      step: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      displayStep: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ).isRequired,
}

export default LeversMolecule
